import { useMemo } from "react";
import { useUser } from "../contexts/UserContext";
import useFeatureFlag from "./useFeatureFlag";
import useFocusMode from "./useFocusMode";
import {
  FEATURE_REGISTRY,
  ALWAYS_AVAILABLE_NAV,
  STANDARD_MODE_EXTRAS,
} from "../data/featureRegistry";

// Re-export from data/navItems.ts for backward compatibility — existing code
// imports these from here. The pure data + buildNavForPrefs function live in
// data/navItems.ts so they can be unit-tested without pulling in Firebase.
export type { NavItem } from "../data/navItems";
export {
  ALL_NAV_ITEMS,
  DEFAULT_NAV_BAR_ITEMS,
  buildNavForPrefs,
} from "../data/navItems";

/**
 * Returns the set of nav item IDs that are currently available based on
 * feature flags and the user's onboarding preferences. Derives entirely
 * from FEATURE_REGISTRY + ALWAYS_AVAILABLE_NAV — to add a new feature
 * to the nav, just add it to the registry.
 */
export function useAvailableNavItems(): Set<string> {
  const userContext = useUser();
  const onboarding = userContext?.userDetails?.onboarding;
  const virtualPetEnabled = useFeatureFlag("virtual_pet");
  const collectionEnabled = useFeatureFlag("collection");
  const { focusModeEnabled, profile } = useFocusMode();

  const isTester = userContext?.userDetails?.tester === true;
  const isAdmin = userContext?.userDetails?.admin === true;

  return useMemo(() => {
    const available = new Set<string>(ALWAYS_AVAILABLE_NAV);

    // Map feature-flag names to their resolved boolean values. Hooks must be
    // called at the top level of the component, but flag *lookups* by name
    // can happen here.
    const flagValues: Record<string, boolean> = {
      virtual_pet: virtualPetEnabled,
      collection: collectionEnabled,
    };

    // Testers/admins get every feature regardless of onboarding prefs.
    if (isTester || isAdmin) {
      for (const f of FEATURE_REGISTRY) {
        // Still respect required feature flags (so admins on a localhost without
        // a flag enabled don't see broken pages).
        if (f.requiredFlag && !flagValues[f.requiredFlag]) continue;
        available.add(f.navItem);
      }
      for (const id of STANDARD_MODE_EXTRAS) available.add(id);
      return available;
    }

    if (focusModeEnabled && profile) {
      // Focus mode: tab-bar nav comes from the focus profile. Account/family
      // are accessed via the top bar icon, so we deliberately omit them here.
      return available;
    }

    // Standard mode — show Account/Family + every registry feature the user
    // explicitly opted into (and that has its required flag, if any).
    for (const id of STANDARD_MODE_EXTRAS) available.add(id);
    for (const f of FEATURE_REGISTRY) {
      if (f.requiredFlag && !flagValues[f.requiredFlag]) continue;
      if (!f.isEnabled(onboarding?.preferences)) continue;
      available.add(f.navItem);
    }

    // Fallback: if the user hasn't picked any features yet (e.g. brand-new
    // signup, anonymous guest, or before they confirm the home-page picker),
    // always surface the core nav items so the bottom bar isn't empty.
    // Otherwise the user would land with just "Today" and have no way to navigate.
    const noFeatureNavSelected = FEATURE_REGISTRY.every(
      (f) => !f.isEnabled(onboarding?.preferences),
    );
    if (noFeatureNavSelected) {
      available.add("notes");
      available.add("tasks");
      available.add("calendar");
    }

    return available;
  }, [virtualPetEnabled, collectionEnabled, onboarding, focusModeEnabled, profile, isTester, isAdmin]);
}
